import React, { useRef } from '@wordpress/element'; 
import {
    getExistingCheckout,
    isOnlyBrandQuery,
    isEmptyFilter,
    getPriceFilteredProducts,
    isOpen,
} from '../util';
import { dutchieQuery } from '../api/api';
import { sentryCapture } from '../errorHandlers';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';
import { consoleDebug } from '../util';
import { useQuery, useInfiniteQuery, useQueries } from '@tanstack/react-query';

export const useCartData = (retailerId, cartCreated = null) => {
    const checkout = getExistingCheckout();

    return useQuery({
        queryKey: [
            'FetchCartDetails',
            {
                checkoutId: checkout?.checkoutId,
                retailerId: checkout?.retailerId,
            },
        ],
        queryFn: () =>
            dutchieQuery('FetchCartDetails', {
                checkoutId: checkout?.checkoutId,
                retailerId: checkout?.retailerId,
            }).then((res) => {
                if (res.hasOwnProperty('errors') && !res?.data?.checkout) {
                    if (res.errors.some((err) => err.message === 'Not Found')) {
                        const error = new Error(res.errors[0].message);
                        error.level = 'warning';
                        sentryCapture(error, res.errors[0]);
                        storage.removeItem(storageItemName);
                        return null;
                    } else {
                        throw new Error();
                    }
                }

                if (res.hasOwnProperty('errors')) {
                    res.errors.level = 'warning';
                    sentryCapture(res.errors);
                }

                return res.data.checkout;
            }),
        enabled: Boolean(
            retailerId &&
            checkout?.checkoutId && // Only enable if there's a valid checkout ID
            checkout?.retailerId === retailerId &&
            (cartCreated !== null ? !cartCreated.current : true)
        ),
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            return true;
        },
        refetchOnMount: false,
    });
};

export const useMenuProducts = (
    retailerId,
    queryArgs,
    { enabled, paginationLimit, productCacheLimit }
) => {
    return useInfiniteQuery({
        queryKey: [retailerId, queryArgs?.queryName, queryArgs?.variables],
        queryFn: ({ pageParam = 0 }) =>
            dutchieQuery(queryArgs.queryName, {
                ...queryArgs.variables,
                limit: productCacheLimit,
                offset: pageParam * paginationLimit,
            }).then((res) => {
                if (
                    res.hasOwnProperty('errors') &&
                    !res?.data?.menu?.products &&
                    !res?.data?.menu?.brands
                ) {
                    throw new Error('No products or brands found');
                } else {
                    if (res.hasOwnProperty('errors')) {
                        res.errors.level = 'warning';
                        sentryCapture(res.errors);
                    }

                    const { products, brands } = res.data.menu || {};

                    return {
                        brands,
                        data: products || [],
                        pageParam: pageParam + 1,
                    };
                }
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.data) {
                return undefined;
            }
            return lastPage.data.length < paginationLimit
                ? undefined
                : lastPage.pageParam;
        },
        enabled,
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            return true;
        },
        staleTime: 3 * 60 * 1000, // Reduce to 3 hours to ensure fresher data
        cacheTime: 4 * 60 * 1000, // Reduce to 4 hours 
        refetchOnMount: true,     // Add this to ensure fresh data on mount
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
            // Don't retry on 429 errors
            if (error?.response?.status === 429) {
                console.warn('Rate limit hit, not retrying');
                return false;
            }
            return failureCount < 2;
        },
        retryDelay: (attempt) =>
            Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    });
};

export const useProductsGridMenu = (
    retailerId,
    queryVars,
    priceFilters,
    pricingType,
    productAtts,
    isRelatedProducts,
    paginationLimit
) => {
    return useQuery({
        queryKey: ['MenuQuery', queryVars, retailerId],
        queryFn: () =>
            dutchieQuery('MenuQuery', queryVars).then((res) => {
                if (
                    res.hasOwnProperty('errors') &&
                    !res?.data?.menu?.products
                ) {
                    throw new Error();
                } else {
                    if (res.hasOwnProperty('errors')) {
                        res.errors.level = 'warning';
                        sentryCapture(res.errors);
                    }

                    return res.data.menu.products;
                }
            }),
        select: (products) =>
            Boolean(priceFilters !== null)
                ? getPriceFilteredProducts(
                    products,
                    priceFilters,
                    pricingType,
                    paginationLimit
                )
                : products,
        enabled: Boolean(
            queryVars !== null && !productAtts && !isRelatedProducts
        ),
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            return true;
        },
        staleTime: Infinity,
    });
};

export const useRelatedProducts = (
    retailerId,
    filter,
    paginationLimit,
    isRelatedProducts,
    getVariables
) => {
    const fetchProducts = async (updatedFilter) => {
        let allProducts = [];
        let currentFilter = updatedFilter;
        while (allProducts.length < paginationLimit) {
            const vars = getVariables(currentFilter);
            try {
                const res = await dutchieQuery('MenuQuery', vars);
                if (res.hasOwnProperty('errors')) {
                    sentryCapture({ ...res.errors, level: 'warning' });
                    if (!res.data?.menu?.products) {
                        throw new Error(
                            'No products available with current filters.'
                        );
                    }
                }
                allProducts = [...allProducts, ...res.data.menu.products];
                if (allProducts.length >= paginationLimit) break;
                currentFilter = {
                    ...currentFilter,
                    strainType: '',
                    brandIds: [],
                };
            } catch (err) {
                sentryCapture({ message: err.message, level: 'warning' });
                break;
            }
        }
        return allProducts.slice(0, paginationLimit);
    };

    return useQuery({
        queryKey: [
            'MenuQuery',
            getVariables(filter),
            'relatedProductsSlider',
            retailerId,
        ],
        queryFn: () => fetchProducts(filter),
        enabled: Boolean(isRelatedProducts),
        staleTime: Infinity,
        throwOnError: false,
    });
};

export const useRetailerData = (retailerId) => {
    return useQuery({
        queryKey: ['RetailerQuery', retailerId],
        queryFn: () =>
            dutchieQuery('RetailerQuery', { retailerId }).then((res) => {
                if (res.hasOwnProperty('errors') && !res?.data?.retailer) {
                    throw new Error();
                }

                if (res.hasOwnProperty('errors')) {
                    res.errors.level = 'warning';
                    sentryCapture(res.errors);
                }

                return res.data.retailer;
            }),
        staleTime: Infinity,
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            return true;
        },
        enabled: Boolean(retailerId),
    });
};

export const useSingleProducts = (retailerId, productAtts) => {
    return useQueries({
        queries:
            productAtts?.map((productId) => ({
                queryKey: ['ProductQuery', productId, retailerId],
                queryFn: () =>
                    dutchieQuery('ProductQuery', {
                        retailerId,
                        productId,
                    }).then((res) => {
                        if (
                            res.hasOwnProperty('errors') &&
                            !res?.data?.product
                        ) {
                            throw new Error();
                        } else {
                            if (res.hasOwnProperty('errors')) {
                                res.errors.level = 'warning';
                                sentryCapture(res.errors);
                            }

                            return res.data.product;
                        }
                    }),
                enabled: Boolean(productAtts),
                staleTime: Infinity,
                onThrowError: (err) => {
                    err.level = 'error';
                    sentryCapture(err);
                    return false;
                },
            })) ?? [],
        combine: (results) => {
            if (results.some((result) => result.isFetched)) {
                return {
                    productsFetched: true,
                    productsData: results.map((result) => result.data),
                    productsRefetchFns: results.map((result) => result.refetch),
                };
            } else {
                return {
                    productsFetched: false,
                    productsData: null,
                    productsRefetchFns: results.map((result) => result.refetch),
                };
            }
        },
    });
};

export const useSpecialsList = (retailerId) => {
    // Add a ref to track if we've already logged
    const hasLoggedRef = useRef(false);
    
    return useQuery({
        queryKey: ['GetSpecialsList', retailerId],
        queryFn: () => {
            return dutchieQuery('GetSpecialsList', { retailerId }).then((res) => {
                if (res.hasOwnProperty('errors') && !res?.data?.specials) {
                    throw new Error('Failed to fetch specials');
                }

                if (res.hasOwnProperty('errors')) {
                    res.errors.level = 'warning';
                    sentryCapture(res.errors);
                }

                return res.data.specials;
            });
        },
        select: (data) => {
            // Ensure data is an array
            if (!Array.isArray(data)) {
                return [];
            }
            
            // Only log once to avoid console spam
            const shouldLog = !hasLoggedRef.current;
            hasLoggedRef.current = true;

            const filtered = data.filter((special) => {
                if (!special) return false;

                if (special.hasOwnProperty('scheduleConfiguration')) {
                    const {
                        days,
                        endDate,
                        endStamp,
                        recurringEndTime,
                        recurringStartTime,
                        setEndDate,
                        startStamp,
                    } = special.scheduleConfiguration || {};

                    // Get the current browser time
                    const current = new Date();
                    const currentISO = current.toISOString();
                    
                    // Get current day in local timezone (0 = Sunday, 1 = Monday, etc.)
                    const currentDay = current.getDay();
                    
                    // Convert current time to hours and minutes for comparison
                    const currentHours = current.getHours();
                    const currentMinutes = current.getMinutes();
                    const currentTimeInMinutes = currentHours * 60 + currentMinutes;
                    
                    // Parse recurring times to compare with current time
                    let startTimeInMinutes = null;
                    let endTimeInMinutes = null;
                    
                    if (recurringStartTime && recurringEndTime) {
                        // Parse times like "04:00 AM" or "11:00 AM"
                        const parseTimeString = (timeStr) => {
                            if (!timeStr) return null;
                            
                            try {
                                const [timePart, ampm] = timeStr.split(' ');
                                let [hours, minutes] = timePart.split(':').map(Number);
                                
                                // Convert 12-hour format to 24-hour
                                if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
                                if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
                                
                                return hours * 60 + minutes; // Convert to minutes since midnight
                            } catch (e) {
                                return null;
                            }
                        };
                        
                        startTimeInMinutes = parseTimeString(recurringStartTime);
                        endTimeInMinutes = parseTimeString(recurringEndTime);
                    }
                    
                    // Debug log to see the time calculations - only for the first special and only once
                    if (shouldLog && special.id === data[0]?.id) {
                        consoleDebug('🔍 SPECIAL FILTERING:', {
                            id: special.id,
                            name: special.name,
                            days: days,
                            currentDay: currentDay,
                            recurringStartTime,
                            recurringEndTime,
                            startTimeInMinutes,
                            endTimeInMinutes,
                            currentTimeInMinutes,
                            currentTime: `${currentHours}:${currentMinutes.toString().padStart(2, '0')}`,
                            startStamp,
                            endStamp,
                            currentISO,
                            isActive: startStamp && startStamp < currentISO,
                            isWithinTimeWindow: startTimeInMinutes === null || endTimeInMinutes === null || 
                                (currentTimeInMinutes >= startTimeInMinutes && 
                                 currentTimeInMinutes <= endTimeInMinutes)
                        });
                    }

                    // Check if the special has started based on startStamp
                    if (startStamp && startStamp < currentISO) {
                        // Check if the special has ended
                        if ((setEndDate || endDate) && (endDate < currentISO || endStamp < currentISO)) {
                            return false;
                        }

                        // Check if specific days are configured and today is included
                        if (days && Array.isArray(days)) {
                            if (!days.includes(currentDay)) {
                                return false;
                            }
                        }
                        
                        // Check time restrictions if both start and end times are provided
                        if (startTimeInMinutes !== null && endTimeInMinutes !== null) {
                            let isWithinWindow;
                            
                            // Handle cases where time range spans midnight
                            if (startTimeInMinutes > endTimeInMinutes) {
                                // Example: 10:00 PM to 4:00 AM
                                isWithinWindow = 
                                    currentTimeInMinutes >= startTimeInMinutes || 
                                    currentTimeInMinutes <= endTimeInMinutes;
                            } else {
                                // Normal case: 9:00 AM to 5:00 PM
                                isWithinWindow = 
                                    currentTimeInMinutes >= startTimeInMinutes && 
                                    currentTimeInMinutes <= endTimeInMinutes;
                            }
                                
                            if (!isWithinWindow) {
                                return false;
                            }
                        }
                        
                        // If we've passed all checks, the special is active
                        return true;
                    }

                    return false;
                }

                return false;
            });

            if (shouldLog) {
                consoleDebug(`Filtered ${data.length} specials down to ${filtered.length} active specials`);
            }
            return filtered || [];
        },
        // Improve caching behavior
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: true, 
        retry: 1,
        enabled: Boolean(retailerId),
        useErrorBoundary: false,
    });
};

export const useSingleProduct = (retailerId, productId, { enabled }) => {
    return useQuery({
        queryKey: [
            'ProductQuery',
            {
                retailerId,
                productId,
            },
        ],
        queryFn: () =>
            dutchieQuery('ProductQuery', {
                retailerId,
                productId,
            }).then((res) => {
                if (res.hasOwnProperty('errors') && !res?.data?.product) {
                    throw new Error();
                } else {
                    if (res.hasOwnProperty('errors')) {
                        res.errors.level = 'warning';
                        sentryCapture(res.errors);
                    }

                    return res.data.product;
                }
            }),
        staleTime: Infinity,
        enabled,
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            return true;
        },
    });
};

export const useOrdersByOrderNumber = (
    retailerId,
    orderNumber,
    handleTracking = null,
    analytics = null
) => {
    return useQuery({
        queryKey: [
            'OrdersByOrderNumber',
            {
                retailerId,
                orderNumber,
            },
        ],
        queryFn: () =>
            dutchieQuery('OrdersByOrderNumber', {
                retailerId,
                orderNumber,
            }).then((res) => {
                if (res.hasOwnProperty('errors') && !res?.data?.orders) {
                    throw new Error();
                } else {
                    if (res.hasOwnProperty('errors')) {
                        res.errors.level = 'warning';
                        sentryCapture(res.errors);
                    }

                    const order = res.data.orders[0];

                    if (res.data.orders?.length > 0) {
                        if (handleTracking) {
                            handleTracking(orderNumber, order, analytics);
                        }

                        storage.removeItem(storageItemName);
                        return order;
                    }
                }
            }),
        staleTime: Infinity,
        enabled: Boolean(orderNumber),
        throwOnError: (err) => {
            err.level = 'fatal';
            sentryCapture(err);
            storage.removeItem(storageItemName);
            return true;
        },
    });
};
